import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { authApiService } from '../services/authApiService'
import { ApiError } from '../services/BaseApiService'
import { colors, fontSize, radius, shadow, fontFamily, componentHeight, transition } from '../styles/theme'

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  if (!token) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: colors.surfaceTinted, fontFamily: fontFamily.sans,
      }}>
        <div style={{ color: colors.error }}>Invalid or missing reset token.</div>
      </div>
    )
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    // Mirrors AuthController.ResetPassword (NewPassword.Length < 8 is rejected).
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return }
    if (password !== confirm) { setError('Passwords do not match'); return }
    setLoading(true)
    try {
      await authApiService.resetPassword(token, password)
      navigate('/login')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Reset failed')
    } finally {
      setLoading(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', height: componentHeight.inputMd, padding: '0 12px',
    border: `1px solid ${colors.border}`, borderRadius: radius.md,
    fontSize: fontSize.sm, color: colors.text, background: colors.surface,
    boxSizing: 'border-box', outline: 'none', fontFamily: fontFamily.sans,
    transition: `border-color ${transition.fast}`,
  }

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: fontSize.sm, fontWeight: 500, color: colors.text,
    marginBottom: 5, letterSpacing: '-0.01em',
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: colors.surfaceTinted, padding: '24px 16px', fontFamily: fontFamily.sans,
    }}>
      {/* Brand */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
        <div style={{ width: 44, height: 44, borderRadius: 10, background: colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width={22} height={22} viewBox="0 0 20 20" fill="none">
            <path d="M4 3h8l4 4v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" fill="#fff" opacity={0.9} />
            <path d="M12 3l4 4h-4V3z" fill="#fff" opacity={0.6} />
            <path d="M7 9h6M7 12h6M7 15h4" stroke="#0F9D92" strokeWidth={1.2} strokeLinecap="round" />
          </svg>
        </div>
        <span style={{ fontSize: '22px', fontWeight: 600, color: colors.text, letterSpacing: '-0.02em' }}>
          Doc<span style={{ color: colors.primary }}>morph</span>ia
        </span>
      </div>

      <div style={{
        background: colors.surface, borderRadius: radius.lg, border: `1px solid ${colors.border}`,
        boxShadow: shadow.md, padding: '32px 32px 28px', width: '100%', maxWidth: 400,
      }}>
        <div style={{ fontSize: fontSize['2xl'], fontWeight: 600, color: colors.text, letterSpacing: '-0.02em', marginBottom: 8 }}>
          Reset password
        </div>
        <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: 24 }}>
          Enter your new password below.
        </div>

        {error && (
          <div style={{
            background: colors.errorLight, color: colors.error, border: `1px solid ${colors.errorBorder}`,
            borderRadius: radius.md, padding: '10px 14px', fontSize: fontSize.sm, marginBottom: 16,
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>New password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              style={inputStyle} required autoFocus autoComplete="new-password" placeholder="8+ characters" />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Confirm new password</label>
            <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
              style={inputStyle} required autoComplete="new-password" />
          </div>
          <button
            type="submit" disabled={loading}
            style={{
              width: '100%', height: componentHeight.btnLg, background: colors.primary,
              color: '#fff', border: 'none', borderRadius: radius.full, fontSize: fontSize.sm,
              fontWeight: 500, cursor: 'pointer', marginTop: 8, fontFamily: fontFamily.sans,
              transition: `background ${transition.fast}`,
            }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.background = colors.primaryHover }}
            onMouseLeave={e => { e.currentTarget.style.background = colors.primary }}
          >
            {loading ? 'Resetting…' : 'Reset password'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: fontSize.sm, color: colors.textSecondary }}>
          <Link to="/login" style={{ color: colors.primary, textDecoration: 'none', fontWeight: 500 }}>
            ← Back to sign in
          </Link>
        </div>
      </div>
    </div>
  )
}
